import { useCallback, useEffect } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";

import { Button } from "../../../components/Button";
import { useAuth } from "../../../components/contexts/AuthContext";
import { isClientReadOnly } from "../../../utils/clientAccess";
import { ReadOnlyClientNotice } from "../ReadOnlyClientNotice";
import { getData } from "../../../components/hooks/getData";
import { ContentPieceView } from "../ContentPieceView";
import { useContentTutorial } from "../ContentTutorialContext";
import { contentListPath, pageFrameSx, piecePath, useToast } from "./ContentPiecePage";
import { usePieceDetail } from "./usePieceDetail";

/** Creates a single piece: topic → base text → network adaptations, then opens its page. */
export function NewPiecePage() {
  const [searchParams] = useSearchParams();
  const configId = searchParams.get("config");
  const navigate = useNavigate();
  const { setPageContext } = useOutletContext();
  const { interceptorReady, user } = useAuth();
  const tutorial = useContentTutorial();
  const { notify, element: toast } = useToast();
  const { saveAdaptationChanges, iterateAdaptation, iteratingAdaptationId } = usePieceDetail(null, {
    notify,
  });

  const { data: config, isLoading } = getData(
    configId ? `content-generation/configs/${configId}/` : null,
    interceptorReady && Boolean(configId),
    [configId]
  );

  const goBack = useCallback(
    () => navigate(contentListPath(false)),
    [navigate]
  );

  useEffect(() => {
    setPageContext({
      title: "",
      showTitle: false,
      rightContent: null,
      breadcrumbItems: [
        { label: "Contenidos", onClick: goBack },
        { label: config?.client_name ? `Nueva pieza · ${config.client_name}` : "Nueva pieza" },
      ],
    });
  }, [setPageContext, goBack, config?.client_name]);

  if (!configId) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Elige primero un cliente en la lista de contenidos.
        </Typography>
        <Button variant="outline" onClick={goBack}>
          Volver a contenidos
        </Button>
      </Box>
    );
  }

  if (isLoading || !config) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isClientReadOnly(user, config.client)) {
    return <ReadOnlyClientNotice clientName={config.client_name} onBack={goBack} />;
  }

  return (
    <Box sx={pageFrameSx}>
      {toast}
      <ContentPieceView
        mode="create"
        onClose={goBack}
        onSaveChanges={saveAdaptationChanges}
        onIterateAdaptation={iterateAdaptation}
        iteratingAdaptationId={iteratingAdaptationId}
        configId={config.id}
        initialLanguage={config.language || "es"}
        onGenerationComplete={(piece) => navigate(piecePath(false, piece.id), { replace: true })}
        isTutorialActive={tutorial.isTutorialActive}
      />
    </Box>
  );
}
